/**
 * Fixed-chrome exclusion for idle perch targets (yap 2026-07-07).
 *
 * The bird kept landing ON the studio's bottom timeline dock, sitting
 * on top of clips mid-edit. Idle perch / nest / corner-fallback targets
 * must never overlap fixed chrome:
 *
 *   - `.studio-timeline-dock` — the bottom dock, including its expanded
 *     tray height. A target inside it is clamped so the bird's feet sit
 *     at the dock's CURRENT top edge, body above.
 *   - `.studio-top-strip` — the top strip. A target inside it is clamped
 *     below the strip's bottom edge.
 *
 * The chrome is discovered by querying the document at clamp time, so
 * the rule tracks the dock's live collapsed/expanded height and degrades
 * gracefully on host pages without studio chrome (the marketing widget):
 * no matching elements → the target is returned untouched.
 *
 * Deliberately NOT applied to pointing flights — flying to an element
 * the user asked about is allowed to go anywhere. Only idle resting
 * positions go through this clamp.
 */

/** Fixed chrome the bird must never perch on. */
export const CHROME_EXCLUSION_SELECTORS = [
  ".studio-timeline-dock",
  ".studio-top-strip",
] as const;

export interface PerchTarget {
  viewportX: number;
  viewportY: number;
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Strict overlap — rects that merely touch edges do not intersect. */
function intersects(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Current viewport rects of visible chrome elements. Tolerates SSR and
 * non-studio hosts (no document / no matches / hidden elements → []).
 */
function visibleChromeRects(): Rect[] {
  if (typeof document === "undefined") return [];
  const rects: Rect[] = [];
  for (const selector of CHROME_EXCLUSION_SELECTORS) {
    const elements = document.querySelectorAll(selector);
    for (const element of Array.from(elements)) {
      const rect = element.getBoundingClientRect();
      // display:none / detached chrome reports a zero-size rect — skip.
      if (rect.width <= 0 || rect.height <= 0) continue;
      rects.push({
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      });
    }
  }
  return rects;
}

/**
 * Clamp a perch target (the bird sprite's CENTER, viewport coords) so
 * the sprite's square never overlaps fixed chrome. Bottom-half chrome
 * (the timeline dock) pushes the bird up — feet at the chrome's top
 * edge; top-half chrome (the top strip) pushes it down below the
 * chrome's bottom edge. Returns the target unchanged when no chrome is
 * present or nothing overlaps.
 */
export function clampPerchTargetToChrome(
  target: PerchTarget,
  spriteSizePx: number,
): PerchTarget {
  const chromeRects = visibleChromeRects();
  if (chromeRects.length === 0) return target;

  const half = spriteSizePx / 2;
  const viewportHeight = typeof window === "undefined" ? 0 : window.innerHeight;
  let x = target.viewportX;
  let y = target.viewportY;

  // Two passes: escaping one chrome band could, in theory, land the
  // sprite inside another. With only a top strip and a bottom dock this
  // settles in a single extra pass; the fixed bound keeps it from ever
  // oscillating on a pathological layout.
  for (let pass = 0; pass < 2; pass += 1) {
    for (const chrome of chromeRects) {
      const sprite: Rect = {
        left: x - half,
        top: y - half,
        right: x + half,
        bottom: y + half,
      };
      if (!intersects(sprite, chrome)) continue;
      const chromeCenterY = (chrome.top + chrome.bottom) / 2;
      if (chromeCenterY >= viewportHeight / 2) {
        // Bottom chrome (timeline dock): feet at its top edge, body above.
        y = chrome.top - half;
      } else {
        // Top chrome (top strip): perch just below its bottom edge.
        y = chrome.bottom + half;
      }
    }
  }
  return { viewportX: x, viewportY: y };
}
